import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';
import { CONTACT_INFO } from '../data/mock';

const Footer = () => {
  const { language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { label: t(language, 'nav.howItWorks'), path: '/how-it-works' },
    { label: t(language, 'nav.services'), path: '/services' },
    { label: t(language, 'nav.fit'), path: '/fit' },
    { label: t(language, 'nav.faq'), path: '/faq' }
  ];

  return (
    <footer style={{ backgroundColor: 'var(--midnight)' }} className="text-pearl/85">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {/* Brand */}
          <div>
            <img
              src="/brand/sin-fondo-3.png"
              alt="BIMCORP MX"
              className="h-9 w-auto mb-5"
              loading="lazy"
              data-testid="footer-logo"
            />
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(254,253,244,0.72)' }}>
              {t(language, 'footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="brand-section-label mb-4" style={{ color: 'rgba(254,253,244,0.9)' }}>{t(language, 'footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm transition-colors"
                    style={{ color: 'rgba(254,253,244,0.85)' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--light-blue)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(254,253,244,0.85)'}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="brand-section-label mb-4" style={{ color: 'rgba(254,253,244,0.9)' }}>{t(language, 'footer.contact')}</h4>
            <div className="space-y-3">
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center space-x-2 text-sm transition-colors"
                style={{ color: 'rgba(254,253,244,0.85)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--light-blue)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(254,253,244,0.85)'}
              >
                <Mail size={16} />
                <span>{CONTACT_INFO.email}</span>
              </a>
              <a
                href={CONTACT_INFO.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat with BIMCORP MX on WhatsApp"
                data-testid="footer-whatsapp-link"
                className="inline-flex items-center gap-1.5 text-sm transition-colors leading-none"
                style={{ color: 'rgba(254,253,244,0.85)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--light-blue)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(254,253,244,0.85)'}
              >
                <WhatsAppIcon size={16} className="flex-shrink-0" />
                <span>{CONTACT_INFO.phone}</span>
              </a>
              <Link
                to="/privacy"
                className="block text-sm transition-colors pt-2"
                data-testid="footer-privacy-link"
                style={{ color: 'rgba(254,253,244,0.85)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--light-blue)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(254,253,244,0.85)'}
              >
                {t(language, 'footer.privacy')}
              </Link>
              <Link
                to="/terms"
                className="block text-sm transition-colors"
                data-testid="footer-terms-link"
                style={{ color: 'rgba(254,253,244,0.85)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--light-blue)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(254,253,244,0.85)'}
              >
                {t(language, 'footer.terms')}
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8" style={{ borderTop: '1px solid rgba(254,253,244,0.15)' }}>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm" style={{ color: 'rgba(254,253,244,0.55)' }}>
              © {currentYear} BIMCORP MX. {t(language, 'footer.allRights')}
            </p>
            <p className="text-xs max-w-2xl text-center md:text-right" style={{ color: 'rgba(254,253,244,0.5)' }}>
              {t(language, 'footer.disclaimer')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
